import React, { Component } from 'react'
import styled, { keyframes } from 'styled-components'
import RemoveIcon from '../icons/Remove'
import Text from '../Text'

const ERROR_COLOR = '#dd0d42'

const Container = styled.div`
  position: relative;
  margin: ${({ margin }) => (margin ? margin : '0.5rem 0')};
  pointer-events: ${props => (props.isReadOnly ? 'none' : 'initial')};
`

const Label = styled.label`
  color: ${({ readOnly, variant, invalid }) =>
    invalid
      ? ERROR_COLOR
      : variant === 'default'
      ? readOnly
        ? '#696969'
        : '#1557bf'
      : variant === 'error'
      ? ERROR_COLOR
      : '#1557bf'};
  font-size: 1rem;
  font-weight: 600;
  position: absolute;
  pointer-events: none;
  left: 1rem;
  top: 1.175rem;
  transition: 0.2s ease all;
`
/* stylelint-disable block-no-empty */
const onAutoFillStart = keyframes`
  from {}
  to {}
`

const InputBox = styled.input`
  font-size: 1rem;
  padding: 1.5rem 1.5rem 0.5rem 1rem;
  display: block;
  width: 100%;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ variant, invalid }) =>
    invalid
      ? ERROR_COLOR
      : variant === 'default'
      ? '#eaeaea'
      : variant === 'error'
      ? ERROR_COLOR
      : '#eaeaea'};
  border-radius: 4px;

  :read-only {
    background-color: #f3f5f7;
    color: #696969;
  }

  :focus {
    outline: none;
    border-color: ${({ variant, invalid }) =>
      invalid
        ? ERROR_COLOR
        : variant === 'default'
        ? '#1557bf'
        : variant === 'error'
        ? ERROR_COLOR
        : '#333333'};
    color: ${({ readOnly, variant }) =>
      variant === 'default' ? '#000' : readOnly ? '#696969' : '#000'};
  }
  :focus
    ~ ${/* sc-selector */ Label},
    :not([value=''])
    ~ ${/* sc-selector */ Label},
    &&.auto-fill
    ~ ${/* sc-selector */ Label} {
    top: 0.5rem;
    font-size: 0.75rem;
    color: ${({ readOnly, variant, invalid }) =>
      invalid
        ? ERROR_COLOR
        : variant === 'default'
        ? readOnly
          ? '#696969'
          : '#1557bf'
        : variant === 'error'
        ? ERROR_COLOR
        : '#333333'};
    border-color: ${({ readOnly, variant, invalid }) =>
      invalid
        ? ERROR_COLOR
        : variant === 'default'
        ? readOnly
          ? '#696969'
          : '#1557bf'
        : variant === 'error'
        ? ERROR_COLOR
        : '#1557bf'};
  }

  &::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }
  &::-ms-reveal {
    display: none;
    width: 0;
    height: 0;
  }

  :-webkit-autofill {
    animation-name: ${onAutoFillStart};
    animation-duration: 0.5s;
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    /* stylelint-disable property-no-vendor-prefix */
    -webkit-appearance: none;
    margin: 0;
  }
`

const ErrorText = styled(Text)`
  text-align: left;
  font-weight: normal;
  display: block;
`
const TextAreaContainer = styled.textarea`
  outline: none;
  appearance: none;
  border-radius: 5px;
  width: 100%;
  min-height: 7rem;
  padding: 0.6rem;
  line-height: 1.2rem;
  font-family: Lato, sans-serif;
  &:focus {
    box-shadow: 0px 0px 3px #a3c2f5;
    border-color: #1454b8;
  }
`

const CharCounter = styled.div`
  font-size: 0.8rem;
  color: #333;
  text-align: right;
  padding: 0.2rem;
`

const ClearIcon = styled(RemoveIcon)`
  position: absolute;
  right: 0.7rem;
  top: 1.6rem;
  cursor: pointer;
`

const StyledRadioInput = styled.input`
  display: none;
  :checked + label::before {
    border: 0.3125rem solid #1557bf;
  }
`

const StyledRadioLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  &::before {
    content: '';
    display: inline-block;
    height: 1rem;
    box-sizing: border-box;
    width: 1rem;
    border-radius: 50%;
    border: ${({ disabled }) =>
      disabled ? '1px solid #d0d0d0' : '1px solid #1557bf'};
    margin-right: 0.56rem;
  }
`

const ShowButton = styled(Text)`
  position: absolute;
  right: 0.7rem;
  top: 1.2rem;
  cursor: pointer;
`

const OptionalText = styled.span`
  color: #696969;
  font-size: 0.6rem;
`

const Radio = ({
  label,
  checked,
  name,
  id,
  value,
  onChange,
  color,
  disabled,
  readOnly,
}) => (
  <React.Fragment>
    <StyledRadioInput
      type="radio"
      name={name}
      id={id}
      checked={checked}
      value={value}
      onChange={onChange}
      color={color}
      readOnly={readOnly}
    />
    <StyledRadioLabel htmlFor={id} disabled={disabled}>
      <Text size="medium" weight="bold" color="#333333">
        {label}
      </Text>
    </StyledRadioLabel>
  </React.Fragment>
)

const TextArea = ({
  maxLength,
  name,
  id,
  disabled,
  readonly,
  value,
  placeholder,
  invalid,
  charCounterTestId,
  testId,
  ...events
}) => {
  const { onChange, onKeyDown, onKeyUp, onFocus } = events

  return (
    <Container>
      {maxLength && (
        <CharCounter data-testid={charCounterTestId || 'charCounter'}>
          {value?.length || 0}/{maxLength}
        </CharCounter>
      )}
      <TextAreaContainer
        value={value}
        name={name}
        id={id}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        readonly={readonly}
        data-testid={testId}
      />
      {Boolean(invalid) && (
        <ErrorText size="small" weight="bold" color={ERROR_COLOR}>
          {invalid}
        </ErrorText>
      )}
    </Container>
  )
}

class Input extends Component {
  constructor(props) {
    super(props)
    this.state = {}
    this.handleTogglePassword = this.handleTogglePassword.bind(this)
    this.handleAutoFill = this.handleAutoFill.bind(this)
  }
  componentDidMount() {
    if (this.props.autoFocus) {
      this.inputRef && this.inputRef.focus()
    }
    if (this.inputRef) {
      this.inputRef.addEventListener('animationend', this.handleAutoFill)
    }
  }
  componentDidUpdate(prevProps) {
    if (prevProps.autoFocus !== this.props.autoFocus) {
      this.inputRef && this.inputRef.focus()
    }
  }

  componentWillUnmount() {
    if (this.inputRef) {
      this.inputRef.removeEventListener('animationend', this.handleAutoFill)
    }
  }

  handleAutoFill() {
    this.setState({ autoFill: true })
  }

  handleTogglePassword() {
    if (!this.state.stateType) {
      this.setState({ stateType: 'text' })
    } else {
      this.setState({
        stateType: this.state.stateType === 'password' ? 'text' : 'password',
      })
    }
  }

  trimLeft(value) {
    return value.replace(new RegExp('^[\\s]+'), '')
  }

  render() {
    const {
      optional,
      label,
      clear,
      value,
      disabled,
      id,
      name,
      type,
      variant,
      required,
      invalid,
      readOnly,
      autoFocus,
      className,
      hideToggleButton,
      maxLength,
      onFocus,
      onBlur,
      pattern,
      margin,
      autoComplete = 'off',
      ...events
    } = this.props

    const { onChange, onKeyDown, onKeyUp } = events
    const { stateType, autoFill } = this.state
    const computedValue = value === undefined || value === null ? '' : value
    const dataTestId = this.props['data-testid'] || 'input'
    return (
      <Container isReadOnly={readOnly} className={className} margin={margin}>
        <InputBox
          pattern={pattern}
          onChange={event => {
            const { value, maxLength } = event.target
            if (maxLength && maxLength > 0) {
              event.target.value = value.slice(0, maxLength)
            }

            if (type !== 'password') {
              const trimValue = this.trimLeft(event.target.value)
              if (event.target.value !== trimValue) {
                event.target.value = trimValue
              }
            }

            onChange && onChange(event)
          }}
          onKeyDown={event => {
            if (this.props.readOnly) {
              return
            }

            if (type === 'number' || type === 'tel') {
              //left, right, delete, backspace need to be fillable
              if (
                event.keyCode === 8 ||
                event.keyCode === 46 ||
                event.keyCode === 37 ||
                event.keyCode === 39
              ) {
                onKeyDown && onKeyDown(event)
                return
              }

              const specialCharacters = ['.', '+', '-', 'e']
              const isSpecialCharacter = specialCharacters.some(
                value => value === event.key
              )
              if (isSpecialCharacter) {
                event.preventDefault()
                return
              }
            }
            onKeyDown && onKeyDown(event)
          }}
          onKeyUp={onKeyUp}
          onFocus={onFocus}
          onBlur={event => {
            if (type !== 'password') {
              const trimValue = event.target.value.trim()
              if (event.target.value !== trimValue) {
                event.target.value = trimValue
                onChange(event)
              }
            }

            onBlur && onBlur(event)
          }}
          name={name}
          type={stateType || type}
          value={computedValue}
          id={id}
          autoComplete={autoComplete}
          variant={variant}
          required={required}
          invalid={Boolean(invalid)}
          data-testid={dataTestId}
          autoFocus={autoFocus}
          className={autoFill ? 'auto-fill ' + (className || '') : className}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          ref={node => {
            this.inputRef = node
          }}
          tabIndex={autoFocus ? '-1' : undefined}
        />
        {type !== 'hidden' && (
          <React.Fragment>
            <Label
              invalid={Boolean(invalid)}
              isReadOnly={readOnly}
              variant={variant}
              data-testid="input-label"
              htmlFor={id || ''}
            >
              {label} {optional ? <OptionalText>(Optional)</OptionalText> : ``}
            </Label>
            {Boolean(value) && Boolean(clear) && (
              <ClearIcon onClick={clear} data-testid="remove-icon" />
            )}
            {!hideToggleButton && Boolean(value) && type === 'password' && (
              <ShowButton
                weight="bold"
                color="#1557bf"
                onClick={this.handleTogglePassword}
              >
                {stateType === 'text' ? 'Hide' : 'Show'}
              </ShowButton>
            )}
            {Boolean(invalid) && (
              <ErrorText size="small" weight="bold" color={ERROR_COLOR}>
                {invalid}
              </ErrorText>
            )}
          </React.Fragment>
        )}
      </Container>
    )
  }
}

Input.defaultProps = {
  variant: 'default',
  disabled: false,
}

export default Input

export { Radio, TextArea }
